/**
 * BulkActionBar + Bulk*Dialog components — extracted from JobApplicantsTable
 * so both JobApplicantsTable and the workspace-wide CandidatesListTable can
 * share the same UI without duplicating markup. Every prop is optional; the
 * caller only wires in the handlers it needs (stage move is job-only, so
 * CandidatesListTable simply passes ``stages=[]`` to hide that button).
 *
 * ────────────────────────────────────────────────────────────────────────
 * testIdPrefix CONTRACT (do not break)
 * ────────────────────────────────────────────────────────────────────────
 * Every component below accepts an optional `testIdPrefix` prop (default
 * "jobtable"). All internal data-testid attributes derive from this prefix,
 * e.g. ``${prefix}-bulk-bar``, ``${prefix}-bulk-message-btn``. Consumers:
 *   • JobApplicantsTable  → prefix = "jobtable"  (jobtable-bulk-bar, …)
 *   • CandidatesListTable → prefix = "candtable" (candtable-bulk-bar, …)
 * E2E tests keyed on these selectors — changing the prefix scheme breaks
 * iteration_98+ tests. If you add a new consumer, pick a new prefix and
 * add corresponding tests. Do NOT re-use "jobtable" or "candtable" for
 * anything else.
 */
import { useEffect, useRef, useState } from "react";
import {
    ArrowRightCircle, Bookmark, GitCompareArrows, Link as LinkIcon, Sparkles,
    Loader2, MessageSquare, Mail, Phone, StickyNote, X,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

/**
 * BulkActionBar — floating pill anchored to the bottom center of the viewport
 * showing the current selection count plus available bulk actions. Any
 * omitted handler hides the corresponding button.
 */
export function BulkActionBar({
    count, stages = [], onMoveStage, onMessage, onInapp, onWhatsApp, onNote, onCompare, onQuickCompare, onSaveShortlist, onAssignJobs, onClear, busy, testIdPrefix = "jobtable",
}) {
    const [stageOpen, setStageOpen] = useState(false);
    const stageRef = useRef(null);
    useEffect(() => {
        if (!stageOpen) return;
        const onDown = (e) => { if (stageRef.current && !stageRef.current.contains(e.target)) setStageOpen(false); };
        window.addEventListener("mousedown", onDown);
        return () => window.removeEventListener("mousedown", onDown);
    }, [stageOpen]);

    const canCompare = count >= 2 && count <= 10;
    // Quick Track compare caps at 6 for readability (drawer would break beyond that).
    const canQuickCompare = count >= 2 && count <= 6;
    const btnCls = "h-8 px-2 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 border border-background/30 hover:bg-background/10 disabled:opacity-40";

    return (
        <div
            className="fixed inset-x-0 bottom-4 z-50 pointer-events-none flex justify-center"
            data-testid={`${testIdPrefix}-bulk-bar`}
        >
            <div className="pointer-events-auto inline-flex items-center gap-2 bg-foreground text-background border border-foreground shadow-lg px-3 py-2">
                <span className="text-xs font-mono uppercase tracking-widest" data-testid={`${testIdPrefix}-bulk-count`}>
                    {count} selected
                </span>
                <span className="h-4 w-px bg-background/30 mx-1" />

                {onMoveStage && stages.length > 0 && (
                    <div className="relative" ref={stageRef}>
                        <button type="button" onClick={() => setStageOpen((v) => !v)} disabled={busy}
                            className={btnCls} data-testid={`${testIdPrefix}-bulk-stage-btn`}>
                            <ArrowRightCircle className="w-3.5 h-3.5" /> Move to Stage
                        </button>
                        {stageOpen && (
                            <div className="absolute bottom-full mb-1 right-0 bg-white text-foreground border border-border shadow-lg min-w-[180px] max-h-[300px] overflow-y-auto"
                                 data-testid={`${testIdPrefix}-bulk-stage-menu`}>
                                {stages.map((s) => (
                                    <button key={s.key} type="button"
                                        onClick={() => { setStageOpen(false); onMoveStage(s.key); }}
                                        className="block w-full text-left px-3 py-2 text-xs hover:bg-secondary/60"
                                        data-testid={`${testIdPrefix}-bulk-stage-${s.key}`}>
                                        {s.label || s.key}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {onAssignJobs && (
                    <button type="button" onClick={onAssignJobs} disabled={busy}
                        className={btnCls}
                        title="Assign selected candidates to jobs"
                        data-testid={`${testIdPrefix}-bulk-assign-jobs-btn`}>
                        <ArrowRightCircle className="w-3.5 h-3.5" /> Assign to Jobs
                    </button>
                )}

                {onCompare && (
                    <button type="button" onClick={onCompare} disabled={busy || !canCompare}
                        className={btnCls}
                        title={canCompare ? "Side-by-side compare" : "Select 2–10 candidates to compare"}
                        data-testid={`${testIdPrefix}-bulk-compare-btn`}>
                        <GitCompareArrows className="w-3.5 h-3.5" /> Compare
                    </button>
                )}

                {onQuickCompare && (
                    <button type="button" onClick={onQuickCompare} disabled={busy || !canQuickCompare}
                        className={`${btnCls} border-amber-400 text-amber-200 hover:bg-amber-500/20`}
                        title={canQuickCompare ? "Quick Track compare (salary + scores + notice + activity side-by-side)" : "Select 2–6 candidates for Quick Track compare"}
                        data-testid={`${testIdPrefix}-bulk-quick-compare-btn`}>
                        <Sparkles className="w-3.5 h-3.5" /> Track Compare
                    </button>
                )}

                {onMessage && (
                    <button type="button" onClick={onMessage} disabled={busy}
                        className={btnCls} data-testid={`${testIdPrefix}-bulk-message-btn`}
                        title="Send email to selected candidates (per-candidate thread)">
                        <Mail className="w-3.5 h-3.5" /> Email
                    </button>
                )}

                {onInapp && (
                    <button type="button" onClick={onInapp} disabled={busy}
                        className={btnCls} data-testid={`${testIdPrefix}-bulk-inapp-btn`}
                        title="Post the same message to each candidate's in-app thread (no email sent)">
                        <MessageSquare className="w-3.5 h-3.5" /> In-app
                    </button>
                )}

                {onWhatsApp && (
                    <button type="button" onClick={onWhatsApp} disabled={busy}
                        className={`${btnCls} border-emerald-400 text-emerald-200 hover:bg-emerald-500/20`}
                        data-testid={`${testIdPrefix}-bulk-whatsapp-btn`}
                        title="Send a WhatsApp template to selected candidates via Interakt">
                        <Phone className="w-3.5 h-3.5" /> WhatsApp
                    </button>
                )}

                {onNote && (
                    <button type="button" onClick={onNote} disabled={busy}
                        className={btnCls} data-testid={`${testIdPrefix}-bulk-note-btn`}>
                        <StickyNote className="w-3.5 h-3.5" /> Add Note
                    </button>
                )}

                {onSaveShortlist && (
                    <button type="button" onClick={onSaveShortlist} disabled={busy}
                        className={btnCls}
                        title="Save selection as a named shortlist with a shareable URL"
                        data-testid={`${testIdPrefix}-bulk-shortlist-btn`}>
                        <Bookmark className="w-3.5 h-3.5" /> Save Shortlist
                    </button>
                )}

                <span className="h-4 w-px bg-background/30 mx-1" />

                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <button type="button" onClick={onClear} disabled={busy}
                    className="h-8 w-8 inline-flex items-center justify-center hover:bg-background/10 disabled:opacity-40"
                    aria-label="Clear selection" data-testid={`${testIdPrefix}-bulk-clear`}>
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}


export function BulkMessageDialog({ open, count, onCancel, onSend, busy, testIdPrefix = "jobtable", mode = "email" }) {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [templates, setTemplates] = useState([]);
    const [templateKey, setTemplateKey] = useState("");
    const [tplLoading, setTplLoading] = useState(false);
    const isEmail = mode === "email";

    useEffect(() => {
        if (!open) { setSubject(""); setBody(""); setTemplateKey(""); return; }
        if (!isEmail) return; // in-app doesn't use email templates
        (async () => {
            setTplLoading(true);
            try {
                const { data } = await api.get("/company/email-templates");
                const list = (data?.items || []).filter((t) => t.enabled !== false);
                setTemplates(list);
            } catch { /* silent — fallback to free-form */ }
            finally { setTplLoading(false); }
        })();
    }, [open, isEmail]);

    // When admin picks a template, prefill the dialog so the recruiter sees
    // what will be sent and can tweak the subject before firing.
    useEffect(() => {
        if (!templateKey) return;
        const t = templates.find((x) => x.key === templateKey);
        if (!t) return;
        setSubject(t.subject || t.default_subject || "");
        // Strip HTML tags for the editable body — recruiter edits plain text
        const html = t.body_html || t.default_body || "";
        const text = html.replace(/<br\s*\/?>(?=\s*)/gi, "\n").replace(/<\/?p>/gi, "\n").replace(/<[^>]+>/g, "").trim();
        setBody(text);
    }, [templateKey, templates]);

    const canSend = isEmail
        ? (subject.trim().length > 0 && body.trim().length > 0)
        : body.trim().length > 0;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
            <DialogContent className="max-w-xl" data-testid={`${testIdPrefix}-bulk-message-dialog`}>
                <DialogHeader>
                    <DialogTitle>
                        {isEmail
                            ? `Send email to ${count} candidate${count === 1 ? "" : "s"}`
                            : `Post in-app message to ${count} candidate${count === 1 ? "" : "s"}`}
                    </DialogTitle>
                    <DialogDescription>
                        {isEmail
                            ? "Each candidate gets their own thread. {candidate_name} / {job_title} placeholders in the template render per recipient. Candidates without an email are logged as 'no_email'."
                            : "The same body is posted to every selected candidate's in-app thread. No email is sent."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    {isEmail && (
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">
                                Template (optional)
                            </label>
                            <select
                                value={templateKey}
                                onChange={(e) => setTemplateKey(e.target.value)}
                                disabled={busy || tplLoading}
                                className="w-full border border-border bg-white px-3 py-2 text-sm rounded-none focus:outline-none focus:border-ikb"
                                data-testid={`${testIdPrefix}-bulk-message-template`}
                            >
                                <option value="">— Write a custom message —</option>
                                {templates.map((t) => (
                                    <option key={t.key} value={t.key}>
                                        {t.name} ({t.category})
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-[11px] text-muted-foreground">
                                Pick a template to prefill subject + body, then edit before sending.
                            </p>
                        </div>
                    )}
                    {isEmail && (
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">Subject</label>
                            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                                placeholder="Quick question about the role" disabled={busy}
                                data-testid={`${testIdPrefix}-bulk-message-subject`} />
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">
                            {isEmail ? "Body" : "Message"}
                        </label>
                        <Textarea value={body} onChange={(e) => setBody(e.target.value)}
                            rows={isEmail ? 8 : 6} placeholder={isEmail
                                ? "Hi {candidate_name}, we'd love to schedule a chat…"
                                : "Thanks for your interest — our team will get back to you shortly."}
                            disabled={busy} data-testid={`${testIdPrefix}-bulk-message-body`} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onCancel} disabled={busy} className="rounded-none" data-testid={`${testIdPrefix}-bulk-message-cancel`}>
                        Cancel
                    </Button>
                    <Button onClick={() => onSend({
                            subject: subject.trim(),
                            body: body.trim(),
                            template_key: templateKey || null,
                            channel: isEmail ? "email" : "inapp",
                        })}
                        disabled={!canSend || busy}
                        className="rounded-none bg-ikb hover:bg-[#001f7a] text-white"
                        data-testid={`${testIdPrefix}-bulk-message-send`}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : (
                            isEmail ? <Mail className="w-4 h-4 mr-1.5" /> : <MessageSquare className="w-4 h-4 mr-1.5" />
                        )}
                        {isEmail ? `Send email to ${count}` : `Post to ${count}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


export function BulkNoteDialog({ open, count, onCancel, onSave, busy, testIdPrefix = "jobtable" }) {
    const [body, setBody] = useState("");
    const [pinned, setPinned] = useState(false);
    useEffect(() => { if (!open) { setBody(""); setPinned(false); } }, [open]);
    const canSave = body.trim().length > 0;
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
            <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-bulk-note-dialog`}>
                <DialogHeader>
                    <DialogTitle>Add note to {count} candidate{count === 1 ? "" : "s"}</DialogTitle>
                    <DialogDescription>
                        The same note will be attached to every selected candidate&apos;s profile,
                        tagged with your name.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">Note</label>
                        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7}
                            placeholder="Shortlisted after screening call — needs approval from hiring manager."
                            disabled={busy} data-testid={`${testIdPrefix}-bulk-note-body`} />
                    </div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)}
                            disabled={busy} className="accent-ikb"
                            data-testid={`${testIdPrefix}-bulk-note-pin`} />
                        Pin this note to the top of each profile
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onCancel} disabled={busy} className="rounded-none" data-testid={`${testIdPrefix}-bulk-note-cancel`}>
                        Cancel
                    </Button>
                    <Button onClick={() => onSave({ body: body.trim(), pinned })}
                        disabled={!canSave || busy}
                        className="rounded-none bg-ikb hover:bg-[#001f7a] text-white"
                        data-testid={`${testIdPrefix}-bulk-note-save`}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <StickyNote className="w-4 h-4 mr-1.5" />}
                        Add to {count}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/**
 * BulkShortlistDialog — save current selection as a named shortlist and
 * copy a shareable public URL. Two-phase: Phase 1 (name + description) →
 * Phase 2 (share URL + Copy + Done). Parent MUST provide both onCancel
 * (Phase 1 cancel) and onDone (Phase 2 dismiss).
 */
export function BulkShortlistDialog({ open, count, onCancel, onDone, onSave, busy, testIdPrefix = "jobtable" }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [saved, setSaved] = useState(null);
    useEffect(() => {
        if (!open) { setName(""); setDescription(""); setIsPublic(true); setSaved(null); }
    }, [open]);
    const canSave = name.trim().length > 0;
    const shareUrl = saved ? `${window.location.origin}/shortlist/${saved.share_token}` : "";
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
            <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-bulk-shortlist-dialog`}>
                {!saved ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>Save {count} candidate{count === 1 ? "" : "s"} as a shortlist</DialogTitle>
                            <DialogDescription>
                                Hand this shortlist off to a hiring manager as a single shareable link
                                instead of pasting a list of names in an email.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">Name</label>
                                <Input value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder="Round 2 shortlist · Priya · Sr Backend"
                                    disabled={busy} data-testid={`${testIdPrefix}-bulk-shortlist-name`} />
                            </div>
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">Description (optional)</label>
                                <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                                    rows={4} placeholder="What's this shortlist for?"
                                    disabled={busy} data-testid={`${testIdPrefix}-bulk-shortlist-desc`} />
                            </div>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}
                                    disabled={busy} className="accent-ikb"
                                    data-testid={`${testIdPrefix}-bulk-shortlist-public`} />
                                Anyone with the link can view (contact info hidden)
                            </label>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={onCancel} disabled={busy} className="rounded-none" data-testid={`${testIdPrefix}-bulk-shortlist-cancel`}>
                                Cancel
                            </Button>
                            <Button
                                onClick={async () => { const s = await onSave({ name, description, is_public: isPublic }); if (s) setSaved(s); }}
                                disabled={!canSave || busy}
                                className="rounded-none bg-ikb hover:bg-[#001f7a] text-white"
                                data-testid={`${testIdPrefix}-bulk-shortlist-save`}>
                                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Bookmark className="w-4 h-4 mr-1.5" />}
                                Save shortlist
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>Shortlist saved</DialogTitle>
                            <DialogDescription>
                                <strong>{saved.name}</strong> · {saved.application_ids?.length || saved.candidate_ids?.length || count} candidate{((saved.application_ids?.length || saved.candidate_ids?.length || count) === 1) ? "" : "s"}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 block mb-1">Shareable link</label>
                                <div className="flex items-center gap-2">
                                    <Input value={shareUrl} readOnly className="rounded-none font-mono text-xs" data-testid={`${testIdPrefix}-bulk-shortlist-url`} />
                                    <Button variant="outline" className="rounded-none" data-testid={`${testIdPrefix}-bulk-shortlist-copy`}
                                        onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }}>
                                        <LinkIcon className="w-4 h-4 mr-1.5" /> Copy
                                    </Button>
                                </div>
                                {!saved.is_public && (
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                        Marked private — only your workspace members can open this URL.
                                    </p>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={onDone} className="rounded-none" data-testid={`${testIdPrefix}-bulk-shortlist-done`}>
                                Done
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
